import { css } from '@emotion/react';
import { cloudinaryUrl } from '@realadvisor/cloudinary';
import { PROPERTY_SNIPPET_EMAIL_CSS } from '@realadvisor/constants';

import { theme } from '@shared/theme/theme';

import type {
  EmailEditorRenderSnippetProps,
  EmailEditorSnippetData,
} from './types';

export const mediaCss = css`
  ${PROPERTY_SNIPPET_EMAIL_CSS}
`;

const CARD_HEIGHT = 120;
const THUMB_WIDTH = 148;

type SnippetView = {
  ctaHref: string;
  ctaLabel: string;
  headline: string;
  locationLine: string;
  livingSurfaceLine: string | null;
  thumbnailSrc: string;
};

const parsePositiveNumber = (
  value: string | number | null | undefined,
): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const ctaLabelFor = (
  type: string,
  brochureUrl: string | undefined,
  t: EmailEditorRenderSnippetProps['t'],
): string => {
  if (brochureUrl != null) {
    return t('Open brochure');
  }
  return type === 'development' ? t('View development') : t('View listing');
};

const roomsLineFor = (
  numberOfRooms: EmailEditorSnippetData['numberOfRooms'],
  t: EmailEditorRenderSnippetProps['t'],
): string | null => {
  if (Array.isArray(numberOfRooms)) {
    const values = numberOfRooms.filter(
      room => parsePositiveNumber(room) != null,
    );
    return values.length ? `${values.join(' - ')} ${t('Rooms')}` : null;
  }

  const count = parsePositiveNumber(numberOfRooms);
  return count != null ? t('roomsShort', { count }) : null;
};

const livingSurfaceLineFor = (
  livingSurface: EmailEditorSnippetData['livingSurface'],
): string | null => {
  if (Array.isArray(livingSurface)) {
    const values = livingSurface
      .map(surface => parsePositiveNumber(surface))
      .filter((surface): surface is number => surface != null);
    return values.length ? values.join(' - ') : null;
  }

  const surface = parsePositiveNumber(livingSurface);
  return surface != null ? String(surface) : null;
};

const thumbnailSrcFor = (thumbnailUrl: string | undefined): string =>
  thumbnailUrl != null &&
  thumbnailUrl.trim() !== '' &&
  thumbnailUrl !== '#snippet.thumbnailUrl'
    ? cloudinaryUrl({ w: 296, h: 240, c: 'fill', f: 'jpg' }, thumbnailUrl)
    : '';

const deriveSnippetView = ({
  type,
  title,
  postcode,
  locality,
  listingUrl,
  thumbnailUrl,
  propertyTypeLabel,
  numberOfRooms,
  livingSurface,
  brochureUrl,
  t,
}: EmailEditorRenderSnippetProps): SnippetView => {
  const roomsLine = roomsLineFor(numberOfRooms, t);
  return {
    ctaHref: brochureUrl ?? listingUrl,
    ctaLabel: ctaLabelFor(type, brochureUrl, t),
    headline:
      [propertyTypeLabel, roomsLine].filter(Boolean).join(' · ') || title,
    locationLine: postcode ? `${locality} (${postcode})` : locality,
    livingSurfaceLine: livingSurfaceLineFor(livingSurface),
    thumbnailSrc: thumbnailSrcFor(thumbnailUrl),
  };
};

export const renderPropertySnippet = (props: EmailEditorRenderSnippetProps) => {
  const { title } = props;
  const {
    ctaHref,
    ctaLabel,
    headline,
    locationLine,
    livingSurfaceLine,
    thumbnailSrc,
  } = deriveSnippetView(props);

  return (
    <table
      border={0}
      cellPadding={0}
      cellSpacing={0}
      style={{
        border: '1px solid #E3E3E3',
        borderRadius: 8,
        overflow: 'hidden',
        width: 680,
        maxWidth: '100%',
        borderCollapse: 'separate',
        backgroundColor: '#FFFFFF',
      }}
      className="propertySnippetV1"
    >
      <tbody>
        <tr>
          {thumbnailSrc !== '' ? (
            <td
              valign="top"
              width={THUMB_WIDTH}
              height={CARD_HEIGHT}
              className="propertySnippetV1_templateColumnContainer"
              style={{ padding: 0, position: 'relative' }}
            >
              <img
                src={thumbnailSrc}
                alt={title}
                width={THUMB_WIDTH}
                height={CARD_HEIGHT}
                style={{
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: THUMB_WIDTH,
                  height: '100%',
                  minHeight: CARD_HEIGHT,
                  objectFit: 'cover',
                }}
                className="propertySnippetV1_columnImage"
              />
              <img
                src={thumbnailSrc}
                alt={title}
                width={THUMB_WIDTH}
                height={CARD_HEIGHT}
                style={{
                  display: 'none',
                  width: THUMB_WIDTH,
                  height: CARD_HEIGHT,
                  objectFit: 'cover',
                }}
                className="propertySnippetV1_columnImageMobile"
              />
            </td>
          ) : (
            <td
              align="center"
              valign="middle"
              width={THUMB_WIDTH}
              height={CARD_HEIGHT}
              className="propertySnippetV1_templateColumnContainer"
              style={{ padding: 0, backgroundColor: '#F0F0F0' }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  boxSizing: 'content-box',
                  border: '2px solid #B0B0B0',
                  borderRadius: 6,
                  display: 'inline-block',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: 13,
                    height: 13,
                    boxSizing: 'content-box',
                    border: '2px solid #B0B0B0',
                    borderRadius: '50%',
                    display: 'inline-block',
                    marginTop: 7,
                  }}
                />
              </div>
            </td>
          )}
          <td
            className="propertySnippetV1_templateColumnContainer"
            valign="top"
            style={{ padding: 0 }}
          >
            <table
              border={0}
              cellPadding={0}
              cellSpacing={0}
              width="100%"
              style={{ height: CARD_HEIGHT }}
            >
              <tbody>
                <tr>
                  <td valign="top" style={{ padding: '12px 8px 12px 16px' }}>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: theme.text.font.bold,
                        color: '#1A1A1A',
                        lineHeight: '20px',
                      }}
                    >
                      {headline}
                    </div>
                    <div
                      style={{ fontSize: 13, color: '#555555', marginTop: 3 }}
                    >
                      {locationLine}
                    </div>
                    {title != null && title !== headline && (
                      <div
                        style={{ fontSize: 12, color: '#8A8A8A', marginTop: 5 }}
                      >
                        {title}
                      </div>
                    )}
                    {livingSurfaceLine != null && (
                      <div
                        style={{ fontSize: 12, color: '#8A8A8A', marginTop: 2 }}
                      >
                        {livingSurfaceLine} m<sup>2</sup>
                      </div>
                    )}
                  </td>
                  <td
                    valign="bottom"
                    align="right"
                    style={{
                      padding: '12px 14px 12px 8px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <a
                      href={ctaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        backgroundColor: '#1DA197',
                        color: '#ffffff',
                        fontSize: 12,
                        fontWeight: theme.text.font.bold,
                        borderRadius: 4,
                        padding: '7px 13px',
                        textDecoration: 'none',
                      }}
                    >
                      {ctaLabel}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
